import { z } from 'zod';

export const purchaseInvoiceItemSchema = z.object({
  id: z.number().optional(),
  item_id: z.number({ required_error: 'Product/Service is required' }),
  quantity: z.coerce.number().gt(0, { message: 'Quantity must be greater than 0' }),
  rate: z.coerce.number().gte(0, { message: 'Rate must be 0 or greater' }),
  discount_percent: z.coerce.number().min(0).max(100).default(0),
  discount_amount: z.coerce.number().min(0).default(0),
  amount: z.coerce.number().min(0),
  description: z.string().optional().nullable(),
  godown_id: z.number({ required_error: 'Warehouse/Godown is required' }), // Crucial for purchase stock
  tax_rate_id: z.number().optional().nullable(),
  // Calculated fields (read-only in form, but part of the object)
  taxable_amount: z.coerce.number().optional(),
  tax_amount: z.coerce.number().optional(),
}).refine(data => !(data.discount_percent > 0 && data.discount_amount > 0), {
  message: "Provide either Discount % or Discount Amount, not both.",
  path: ["discount_percent"],
});

export const purchaseInvoiceSchema = z.object({
  voucher_type_id: z.number({ required_error: 'Voucher type is required' }),
  party_id: z.number({ required_error: 'Supplier/Vendor is required' }),
  site_id: z.number({ required_error: 'Site is required' }),
  transaction_date: z.date({ required_error: 'Date is required' }),
  due_date: z.date().optional().nullable(),
  reference_number: z.string().min(1, { message: 'Supplier Invoice No. is required'}),
  narration: z.string().optional().nullable(),
  total_amount: z.number().min(0),
  discount_type: z.enum(['percentage', 'fixed']).optional().nullable(),
  discount_value: z.number().min(0).optional().nullable(),
  items: z.array(purchaseInvoiceItemSchema)
    .min(1, { message: 'At least one item is required' })
    .refine(items => items.every(item => item.godown_id !== undefined && item.godown_id !== null), {
       message: 'Warehouse/Godown must be selected for every item.',
       path: ['items'],
    }),
}).refine((data) => {
  // Validate discount percentage doesn't exceed 100%
  if (data.discount_type === 'percentage' && data.discount_value && data.discount_value > 100) {
    return false;
  }
  // Validate fixed discount doesn't exceed total amount
  if (data.discount_type === 'fixed' && data.discount_value && data.discount_value > data.total_amount) {
    return false;
  }
  return true;
}, {
  message: "Discount percentage cannot exceed 100% or fixed discount cannot exceed total amount",
  path: ["discount_value"],
});

export type PurchaseInvoiceFormValues = z.infer<typeof purchaseInvoiceSchema>;
export type PurchaseInvoiceItemFormValues = z.infer<typeof purchaseInvoiceItemSchema>; 